/**
 * The Jobs buyer session in the VRI apps (`vri_job_client_session`).
 *
 * A separate credential from the VRI account: a `job_client` bearer token the
 * buyer API issues for 12 hours with no refresh. It is kept only in the native
 * Keychain/Keystore (no plaintext mirror), belongs to one API origin and one
 * signed-in VRI account, and is dropped when either differs, when it expires,
 * on a 401/403, on the buyer's own Sign out, and on app sign-out.
 */
import { z } from 'zod';
import { readSecureOnlyItem, removeSecureOnlyItem, writeSecureOnlyItem } from '../../../../vrs-auth/secureStorage';
import { mobileLog } from '../../logging';
export const JOB_CLIENT_SESSION_KEY = 'vri_job_client_session';
/** The server's access-token lifetime; the token carries its own `exp` too. */
export const JOB_CLIENT_SESSION_MAX_AGE_MS = 12 * 60 * 60 * 1000;
/** Treat a session this close to its end as already over. */
const EXPIRY_MARGIN_MS = 60 * 1000;
const CLOCK_SKEW_MS = 5 * 60 * 1000;
const sessionSchema = z.object({
    consumerId: z.string().min(1).max(200),
    contactId: z.string().min(1).max(200),
    email: z.string().max(254),
    expiresAt: z.number().finite(),
    issuedAt: z.number().finite(),
    name: z.string().max(200).nullable(),
    origin: z.string().min(1).max(300),
    owner: z.string().min(1).max(200),
    tenantId: z.string().min(1).max(80),
    token: z.string().min(20).max(8192),
    version: z.literal(1)
});
export function jobClientSessionFromSignIn(receipt, scope, now) {
    const latest = now + JOB_CLIENT_SESSION_MAX_AGE_MS;
    return {
        consumerId: receipt.claims.consumerId,
        contactId: receipt.claims.contactId,
        email: receipt.email,
        expiresAt: receipt.claims.expiresAt === null ? latest : Math.min(receipt.claims.expiresAt, latest),
        issuedAt: now,
        name: receipt.name,
        origin: scope.origin,
        owner: scope.owner,
        tenantId: receipt.claims.tenantId,
        token: receipt.token,
        version: 1
    };
}
/** The stored session when it is still this account's, on this origin, and unexpired; otherwise null. */
export function checkedJobClientSession(raw, scope, now) {
    let value = raw;
    if (typeof raw === 'string') {
        try {
            value = JSON.parse(raw);
        }
        catch {
            return null;
        }
    }
    const parsed = sessionSchema.safeParse(value);
    if (!parsed.success || !scope.origin || !scope.owner) {
        return null;
    }
    const session = parsed.data;
    if (session.origin !== scope.origin || session.owner !== scope.owner
        || session.expiresAt <= now + EXPIRY_MARGIN_MS
        || session.issuedAt > now + CLOCK_SKEW_MS
        || now - session.issuedAt >= JOB_CLIENT_SESSION_MAX_AGE_MS) {
        return null;
    }
    return session;
}
const clearListeners = new Set();
/** A mounted Jobs screen forgets its in-memory session when app sign-out clears the stored one. */
export function subscribeJobClientSessionCleared(listener) {
    clearListeners.add(listener);
    return () => {
        clearListeners.delete(listener);
    };
}
/** Null when absent, unreadable, or not this account's; a stale one is removed. */
export async function loadJobClientSession(scope, now = Date.now()) {
    let raw;
    try {
        raw = await readSecureOnlyItem(JOB_CLIENT_SESSION_KEY);
    }
    catch {
        mobileLog('warn', 'vri_jobs_session_read_failed', {});
        return null;
    }
    if (!raw) {
        return null;
    }
    const session = checkedJobClientSession(raw, scope, now);
    if (!session) {
        await clearJobClientSession();
    }
    return session;
}
/** Throws when the device cannot keep it; the caller then holds it in memory only. */
export async function saveJobClientSession(session) {
    await writeSecureOnlyItem(JOB_CLIENT_SESSION_KEY, JSON.stringify(session));
}
/** True when the stored session is confirmed gone. */
export async function clearJobClientSession() {
    try {
        await removeSecureOnlyItem(JOB_CLIENT_SESSION_KEY);
        return true;
    }
    catch {
        mobileLog('warn', 'vri_jobs_session_clear_failed', {});
        return false;
    }
}
/** App sign-out and account replacement: forget the buyer session everywhere; never blocks sign-out. */
export function beginJobClientSessionCleanup() {
    for (const listener of Array.from(clearListeners)) {
        try {
            listener();
        }
        catch {
            mobileLog('warn', 'vri_jobs_session_view_cleanup_failed', {});
        }
    }
    void clearJobClientSession();
}
